/**
 * FERP Portal - Dashboard Page
 *
 * View-layer-aware dashboard with REAL GraphQL data:
 * - User View: Shows assigned tasks and task statistics
 * - Admin View: Shows team metrics and queue status
 * - Architect View: Shows primitive grid and system health
 *
 * CONSTRAINTS:
 * - NO hardcoded data (fetched via Repository Pattern)
 * - NO direct useQuery<Record<string, any>>(use generated hooks)
 * - Multi-tenant security via useTenantId()
 * - Uses ONLY available backend queries
 */

import React, { useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Skeleton,
  Button,
} from '@mantine/core';
import {
  IconChecklist,
  IconAlertTriangle,
  IconUsers,
  IconTopologyStar3,
  IconAlertCircle,
  IconRocket,
  IconSettings,
  IconUpload,
  IconListCheck,
  IconLoader,
} from '@tabler/icons-react';

import { useAuth, useViewLayer } from '@/auth/AuthContext';
import { useTenantId } from '@/auth';
import { getIcon } from '@/utils/iconMap';
import {
  getAllPrimitives,
  getPrimitivesByPhase,
  LIFECYCLE_PHASES,
  PHASE_CONFIG,
} from '@/config/primitive-metadata';
import {
  useListTasksQuery,
  useListActorsQuery,
  useListWorkflowsQuery,
  useListQueuesQuery,
  useGetTaskStatisticsQuery,
  useGetAllowedNavigationQuery,
  useGetTenantSubgraphConfigQuery,
  useGetTeamWorkspaceSummaryQuery,
  useGetSystemTenantStatusQuery,
  GqlTaskStatus,
  GqlSafetyLevel,
  GqlOnboardingStatus,
  ViewLayer as GqlViewLayer,
  GqlTraversalMode,
} from '@/generated/graphql';
import { useNavigate } from 'react-router-dom';
import { TeamMemberCard, type PresenceStatus } from '@ferp/workflow-forms';

// Enrollment prompt banner for unenrolled users (FERP-PII-052)
import { useEnrollment, SkipReason } from '../enrollment/hooks/useEnrollment';
import { EnrollmentPromptBanner } from '../enrollment/components/EnrollmentPromptBanner';

// DRR-134/135: Person-centered traversal toggle
import { TraversalToggleLoader } from '@/components/navigation/TraversalToggle';
import { MyPeoplePage } from './people/MyPeoplePage';
import { useMyActor } from '@/hooks/useMyActor';

// ============================================================================
// TYPES
// ============================================================================

interface UserStats {
  tasksAssigned: number;
  tasksOverdue: number;
  completedThisWeek: number;
}

interface AdminStats {
  activeTeamMembers: number;
  unassignedTasks: number;
  slaAtRisk: number;
  avgCompletionTime: string;
}

interface ArchitectStats {
  activePrimitives: number;
  activeWorkflows: number;
  enabledSubgraphs: number;
}

// Data-driven safety level colors (derived from GqlSafetyLevel enum)
const SAFETY_LEVEL_COLORS: Record<string, string> = {
  [GqlSafetyLevel.CRITICAL]: 'red',
  [GqlSafetyLevel.HIGH]: 'orange',
  [GqlSafetyLevel.STANDARD]: 'yellow',
  [GqlSafetyLevel.LOW]: 'green',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate average completion time from tasks
 */
function calculateAvgCompletionTime(avgDurationMs: number): string {
  if (avgDurationMs <= 0) return 'N/A';

  const avgDays = avgDurationMs / (1000 * 60 * 60 * 24);

  if (avgDays < 1) {
    const avgHours = avgDurationMs / (1000 * 60 * 60);
    return `${avgHours.toFixed(1)} hours`;
  }
  return `${avgDays.toFixed(1)} days`;
}

// ============================================================================
// DASHBOARD PAGE COMPONENT
// ============================================================================

export function DashboardPage() {
  const { user, tenant } = useAuth();
  const viewLayer = useViewLayer();
  const tenantId = useTenantId();
  const navigate = useNavigate();

  // DRR-135: traversal mode — null until backend resolves initial value
  const [activeTraversalMode, setActiveTraversalMode] = React.useState<GqlTraversalMode | null>(null);

  // Check enrollment status for biometric protection (FERP-PII-052)
  const {
    isEnrolled,
    prompt: enrollmentPrompt,
    skipEnrollment,
  } = useEnrollment();

  // Handler for dismissing/skipping enrollment banner
  const handleSkipEnrollment = async (reason: SkipReason) => {
    await skipEnrollment(reason);
  };

  // DRR-061: Get current user's actor and team data (canonical hook)
  const { teamId: myTeamId } = useMyActor();

  const { data: teamSummaryData, loading: teamLoading } = useGetTeamWorkspaceSummaryQuery({
    variables: { teamId: myTeamId ?? '' },
    skip: !myTeamId || viewLayer !== 'user',
    pollInterval: 60000,
  });

  // Fetch task statistics
  const {
    data: taskStatsData,
    loading: taskStatsLoading,
    error: taskStatsError,
  } = useGetTaskStatisticsQuery({
    skip: !tenantId,
  });

  // Fetch tasks for user and admin stats
  const {
    data: tasksData,
    loading: tasksLoading,
    error: tasksError,
  } = useListTasksQuery({
    variables: { limit: 100 },
    skip: !tenantId,
  });

  // Fetch actors for admin team member count
  const {
    data: actorsData,
    loading: actorsLoading,
    error: actorsError,
  } = useListActorsQuery({
    variables: { limit: 100 },
    skip: !tenantId || viewLayer === 'user',
  });

  // Fetch workflows for architect stats
  const {
    data: workflowsData,
    loading: workflowsLoading,
    error: workflowsError,
  } = useListWorkflowsQuery({
    variables: { limit: 100 },
    skip: !tenantId,
  });

  // Fetch queues for admin stats
  const {
    data: queuesData,
    loading: queuesLoading,
  } = useListQueuesQuery({
    variables: { tenantId: tenantId ?? '', limit: 100 },
    skip: !tenantId || viewLayer === 'user',
  });

  // Fetch subgraph config for architect stats (replaces hardcoded 4/4)
  const { data: subgraphConfigData } = useGetTenantSubgraphConfigQuery({
    variables: { tenantId: tenantId ?? '' },
    skip: !tenantId || viewLayer !== 'architect',
  });

  // DRR-073: Fetch tenant onboarding status — authoritative gate for setup card
  // Only architect needs this (requires tenant:admin scope)
  const { data: tenantStatusData } = useGetSystemTenantStatusQuery({
    variables: { tenantId: tenantId ?? '' },
    skip: !tenantId || viewLayer !== 'architect',
  });
  const onboardingStatus = tenantStatusData?.systemTenant?.onboardingStatus;

  // DRR-102: Scope-filtered routes — determines which stat cards are visible
  const gqlLayer = viewLayer === 'user' ? GqlViewLayer.USER
    : viewLayer === 'admin' ? GqlViewLayer.ADMIN
    : GqlViewLayer.ARCHITECT;
  const { data: allowedNavData } = useGetAllowedNavigationQuery({
    variables: { tenantId: tenantId ?? '', layer: gqlLayer },
    skip: !tenantId,
  });
  // allowedNavData.routes used by QuickAccessCards — data-driven dashboard content

  // Compute user stats from real data
  const userStats = useMemo<UserStats>(() => {
    const taskStats = taskStatsData?.taskApiGetTaskStatistics;
    if (!taskStats) {
      return { tasksAssigned: 0, tasksOverdue: 0, completedThisWeek: 0 };
    }

    return {
      tasksAssigned: (taskStats.pendingCount ?? 0) + (taskStats.inProgressCount ?? 0),
      tasksOverdue: taskStats.failedCount ?? 0,
      completedThisWeek: taskStats.completedCount ?? 0,
    };
  }, [taskStatsData]);

  // Compute admin stats from real data
  const adminStats = useMemo<AdminStats>(() => {
    const taskStats = taskStatsData?.taskApiGetTaskStatistics;
    const actors = actorsData?.listActors?.items ?? [];
    const tasks = tasksData?.taskApiListTasks?.items ?? [];

    const activeTeamMembers = actors.filter((a) => a.active && a.available).length;

    const unassignedTasks = tasks.filter(
      (t) =>
        !t.assignedActor?.id &&
        t.status !== GqlTaskStatus.COMPLETED &&
        t.status !== GqlTaskStatus.CANCELLED
    ).length;

    // Using failed tasks as SLA at risk proxy
    const slaAtRisk = taskStats?.failedCount ?? 0;

    const avgCompletionTime = calculateAvgCompletionTime(Number(taskStats?.avgDurationMs ?? 0));

    return {
      activeTeamMembers: activeTeamMembers || 0,
      unassignedTasks,
      slaAtRisk,
      avgCompletionTime,
    };
  }, [taskStatsData, actorsData, tasksData]);

  // Compute architect stats from real data
  const architectStats = useMemo<ArchitectStats>(() => {
    const workflows = workflowsData?.listWorkflows?.items ?? [];
    const activeWorkflows = workflows.filter((w) => w.active).length;
    const enabledSubgraphs = subgraphConfigData?.getTenantSubgraphConfig?.enabledSubgraphIds?.length ?? 0;

    return {
      activePrimitives: getAllPrimitives().length,
      activeWorkflows: activeWorkflows || 0,
      enabledSubgraphs,
    };
  }, [workflowsData, subgraphConfigData]);

  // Check loading state
  const isLoading =
    taskStatsLoading ||
    tasksLoading ||
    (viewLayer !== 'user' && (actorsLoading || queuesLoading)) ||
    (viewLayer === 'architect' && workflowsLoading);

  // Check for errors
  const hasError = taskStatsError || tasksError || actorsError || workflowsError;

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>
              {activeTraversalMode === GqlTraversalMode.PERSON_CENTERED ? 'My People' : null}
              {viewLayer === 'user' && activeTraversalMode !== GqlTraversalMode.PERSON_CENTERED ? 'My Dashboard' : null}
              {viewLayer === 'admin' && activeTraversalMode !== GqlTraversalMode.PERSON_CENTERED ? 'Team Dashboard' : null}
              {viewLayer === 'architect' && 'System Dashboard'}
            </Title>
            <Text c="dimmed" size="sm">
              Welcome back, {user?.displayName || 'User'}
            </Text>
          </div>
          {viewLayer !== 'architect' && (
            <TraversalToggleLoader
              value={activeTraversalMode}
              onChange={(m) => setActiveTraversalMode(m as GqlTraversalMode)}
              onModeResolved={(m) => setActiveTraversalMode((prev) => prev ?? (m as GqlTraversalMode))}
            />
          )}
        </Group>

        {/* Enrollment Prompt Banner (FERP-PII-052) */}
        {!isEnrolled && enrollmentPrompt && (
          <EnrollmentPromptBanner
            prompt={enrollmentPrompt}
            onDismiss={() => handleSkipEnrollment(SkipReason.REMIND_LATER)}
            onSkip={handleSkipEnrollment}
          />
        )}

        {/* Error Alert */}
        {hasError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Data Loading Error"
            color="red"
            variant="light"
          >
            Some dashboard data could not be loaded. Showing available data.
          </Alert>
        )}

        {/* DRR-073: Onboarding status gate — authoritative lifecycle field */}
        {/* Architect: actionable setup card. Admin: read-only status. */}
        {onboardingStatus === GqlOnboardingStatus.PENDING && viewLayer === 'architect' && (
          <GettingStartedCard viewLayer={viewLayer} onSetup={() => navigate('/setup')} />
        )}
        {onboardingStatus === GqlOnboardingStatus.PENDING && viewLayer === 'admin' && (
          <TenantBeingConfiguredCard />
        )}

        {/* Person-centered view — DRR-134/135 */}
        {activeTraversalMode === GqlTraversalMode.PERSON_CENTERED && (
          <MyPeoplePage />
        )}

        {/* User View Stats (task-centric or mode not yet resolved) */}
        {viewLayer === 'user' && activeTraversalMode !== GqlTraversalMode.PERSON_CENTERED && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <StatCard
                title="Assigned Tasks"
                value={userStats.tasksAssigned}
                icon={IconChecklist}
                color="blue"
                loading={isLoading}
                to="/ferp-core/tasks"
              />
              <StatCard
                title="Needs Attention"
                value={userStats.tasksOverdue}
                icon={IconAlertTriangle}
                color="red"
                loading={isLoading}
                to="/ferp-core/tasks"
              />
              <StatCard
                title="Completed"
                value={userStats.completedThisWeek}
                icon={IconChecklist}
                color="green"
                loading={isLoading}
                to="/ferp-core/tasks"
              />
            </SimpleGrid>

            {/* Quick Access — data-driven from allowedNavigation nav items */}
            <QuickAccessCards navItems={allowedNavData?.allowedNavigation?.navItems ?? []} />

            {/* My Team Summary (DRR-061) */}
            <TeamSummaryCard
              teamData={teamSummaryData?.getTeamWorkspace ?? null}
              loading={teamLoading}
              hasTeam={myTeamId != null}
              onViewAll={() => navigate('/ferp-core/team')}
            />
          </>
        )}

        {/* Admin View Stats */}
        {viewLayer === 'admin' && activeTraversalMode !== GqlTraversalMode.PERSON_CENTERED && (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <StatCard
              title="Active Team Members"
              value={adminStats.activeTeamMembers}
              icon={IconUsers}
              color="blue"
              loading={isLoading}
              to="/ferp-core/admin/actors"
            />
            <StatCard
              title="Unassigned Tasks"
              value={adminStats.unassignedTasks}
              icon={IconChecklist}
              color="yellow"
              loading={isLoading}
              to="/ferp-core/tasks"
            />
            <StatCard
              title="SLA At Risk"
              value={adminStats.slaAtRisk}
              icon={IconAlertTriangle}
              color="orange"
              loading={isLoading}
              to="/ferp-core/tasks"
            />
            <Card withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Avg. Completion Time
              </Text>
              {isLoading ? (
                <Skeleton height={28} mt="xs" width={80} />
              ) : (
                <Text size="xl" fw={700} mt="xs">
                  {adminStats.avgCompletionTime}
                </Text>
              )}
            </Card>
          </SimpleGrid>
        )}

        {/* Architect View Stats */}
        {viewLayer === 'architect' && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
              <StatCard
                title="Active Primitives"
                value={architectStats.activePrimitives}
                icon={IconTopologyStar3}
                color="violet"
                description={`${architectStats.activePrimitives} available`}
                loading={isLoading}
                to="/architect/primitives"
              />
              <StatCard
                title="Active Workflows"
                value={architectStats.activeWorkflows}
                icon={IconChecklist}
                color="blue"
                loading={isLoading}
                to="/ferp-core/workflows"
              />
              <Card withBorder>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Enabled Subgraphs
                </Text>
                {isLoading ? (
                  <Skeleton height={28} mt="xs" width={40} />
                ) : (
                  <Text size="xl" fw={700} mt="xs">
                    {architectStats.enabledSubgraphs}
                  </Text>
                )}
                <Text size="xs" c="dimmed">federated subgraphs</Text>
              </Card>
              <Card withBorder>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Safety Level
                </Text>
                <Group mt="xs">
                  <Badge
                    size="lg"
                    color={SAFETY_LEVEL_COLORS[tenant?.safetyLevel ?? ''] ?? 'gray'}
                  >
                    {tenant?.safetyLevel || 'STANDARD'}
                  </Badge>
                </Group>
              </Card>
            </SimpleGrid>

            {/* 5×5 Primitive Grid Preview — data-driven from primitive-metadata.ts */}
            <Card withBorder>
              <Title order={4} mb="md">
                {architectStats.activePrimitives}-Primitive System (5×5 Grid)
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Complete implementation available in Architect → Primitives
              </Text>
              <SimpleGrid cols={5} spacing="xs">
                {getAllPrimitives().map((primitive) => (
                  <Badge
                    key={primitive.id}
                    variant="light"
                    color={primitive.phaseColor}
                    size="sm"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(primitive.path)}
                  >
                    {primitive.name}
                  </Badge>
                ))}
              </SimpleGrid>
              <Group mt="sm" gap="xs">
                {LIFECYCLE_PHASES.map((phase) => (
                  <Badge key={phase} variant="outline" color={PHASE_CONFIG[phase].color}>
                    {PHASE_CONFIG[phase].label}
                  </Badge>
                ))}
              </Group>
            </Card>
          </>
        )}

        {/* DRR-174: Enrollment Quick Actions — from active product configs */}
        {/* TODO: Replace hardcoded button with product metadata query once
            formFragments query is wired. For now, single "New Resident Inquiry"
            button triggers createInstance + startInstance for wf-4-0-033. */}
        <Card withBorder>
          <Group justify="space-between" mb="sm">
            <Title order={4}>Quick Actions</Title>
          </Group>
          <Group gap="sm">
            <Button
              leftSection={<IconRocket size={16} />}
              variant="light"
              color="teal"
              onClick={() => navigate('/ferp-core/instances?action=enroll&workflow=wf-4-0-033')}
            >
              New Resident Inquiry
            </Button>
          </Group>
        </Card>

        {/* Queue Status (Admin and Architect — not in person-centered mode) */}
        {(viewLayer === 'admin' || viewLayer === 'architect') && activeTraversalMode !== GqlTraversalMode.PERSON_CENTERED && queuesData?.listQueues?.items && (
          <Card withBorder>
            <Title order={4} mb="md">Queue Status</Title>
            {queuesLoading ? (
              <Stack gap="xs">
                <Skeleton height={20} />
                <Skeleton height={20} />
                <Skeleton height={20} />
              </Stack>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
                {queuesData.listQueues.items.slice(0, 4).map((queue) => (
                  <Card key={queue.id} withBorder p="sm">
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{queue.queueName}</Text>
                        <Text size="xs" c="dimmed">
                          Size: {queue.currentSize ?? 0}
                        </Text>
                      </div>
                      <Badge
                        color={queue.currentSize === 0 ? 'gray' : 'green'}
                        size="sm"
                      >
                        {queue.queueType}
                      </Badge>
                    </Group>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Card>
        )}
      </Stack>
    </Container>
  );
}

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ size?: number | string }>;
  color: string;
  description?: string;
  loading?: boolean;
  to?: string;
}

function StatCard({ title, value, icon: Icon, color, description, loading, to }: StatCardProps) {
  const navigate = useNavigate();

  return (
    <Card
      withBorder
      style={to ? {
        cursor: 'pointer',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
      } : undefined}
      onMouseEnter={to ? (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      } : undefined}
      onMouseLeave={to ? (e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      } : undefined}
      onClick={to ? () => navigate(to) : undefined}
    >
      <Group justify="space-between">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {title}
          </Text>
          {loading ? (
            <Skeleton height={28} mt="xs" width={40} />
          ) : (
            <Text size="xl" fw={700} mt="xs">
              {value}
            </Text>
          )}
          {description && (
            <Text size="xs" c="dimmed">
              {description}
            </Text>
          )}
        </div>
        <ThemeIcon color={color} variant="light" size={38} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

// ============================================================================
// QUICK ACCESS CARDS — data-driven from allowedNavigation routes
// ============================================================================

interface QuickAccessCardsProps {
  navItems: Array<{ id: string; path: string; label: string; icon: string; parentId?: string | null }>;
}

/** Clickable nav cards with hover animation. Content from backend, zero hardcoded paths. */
function QuickAccessCards({ navItems }: QuickAccessCardsProps) {
  const navigate = useNavigate();

  // Show leaf items only (skip parents and dashboard)
  const cards = navItems.filter(n =>
    !n.path.includes('/dashboard') && !navItems.some(child => child.parentId === n.id)
  );

  if (cards.length === 0) return null;

  return (
    <Card withBorder>
      <Text size="sm" fw={600} mb="sm">Quick Access</Text>
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }}>
        {cards.map((item) => {
          const NavIcon = getIcon(item.icon);

          return (
            <Card
              key={item.id}
              withBorder
              p="sm"
              style={{ cursor: 'pointer', transition: 'transform 150ms ease, box-shadow 150ms ease' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '';
              }}
              onClick={() => navigate(item.path)}
            >
              <Group gap="xs">
                <ThemeIcon variant="light" size="md" radius="md">
                  <NavIcon size={16} />
                </ThemeIcon>
                <Text size="sm" fw={500}>{item.label}</Text>
              </Group>
            </Card>
          );
        })}
      </SimpleGrid>
    </Card>
  );
}

// ============================================================================
// TEAM SUMMARY CARD (DRR-061)
// ============================================================================

interface TeamSummaryCardProps {
  teamData: {
    teamName: string;
    memberCount: number;
    onlineCount: number;
    members: Array<{
      id: string;
      name: string;
      actorType: string;
      workload: number;
      maxCapacity: number;
    }>;
    presence: Array<{
      actorId: string;
      status: string;
    }>;
  } | null;
  loading: boolean;
  hasTeam: boolean;
  onViewAll: () => void;
}

function TeamSummaryCard({ teamData, loading, hasTeam, onViewAll }: TeamSummaryCardProps) {
  if (!hasTeam) {
    return (
      <Card withBorder>
        <Group justify="space-between">
          <Group gap="sm">
            <ThemeIcon color="blue" variant="light" size={38} radius="md">
              <IconUsers size={22} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={600}>My Team</Text>
              <Text size="xs" c="dimmed">Not assigned to a team</Text>
            </div>
          </Group>
        </Group>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card withBorder>
        <Group justify="space-between" mb="sm">
          <Skeleton height={20} width={120} />
          <Skeleton height={20} width={80} />
        </Group>
        <SimpleGrid cols={{ base: 2, sm: 3 }}>
          <Skeleton height={60} radius="md" />
          <Skeleton height={60} radius="md" />
          <Skeleton height={60} radius="md" />
        </SimpleGrid>
      </Card>
    );
  }

  if (!teamData) return null;

  // Build presence lookup
  const presenceMap = new Map<string, PresenceStatus>();
  for (const p of teamData.presence) {
    presenceMap.set(p.actorId, p.status as PresenceStatus);
  }

  // Show up to 6 members
  const displayMembers = teamData.members.slice(0, 6);

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <ThemeIcon color="blue" variant="light" size={38} radius="md">
            <IconUsers size={22} />
          </ThemeIcon>
          <div>
            <Text size="sm" fw={600}>{teamData.teamName}</Text>
            <Text size="xs" c="dimmed">
              {teamData.onlineCount} online of {teamData.memberCount} members
            </Text>
          </div>
        </Group>
        <Text
          size="xs"
          c="blue"
          fw={500}
          style={{ cursor: 'pointer' }}
          onClick={onViewAll}
        >
          View All →
        </Text>
      </Group>
      <SimpleGrid cols={{ base: 2, sm: 3 }}>
        {displayMembers.map((member) => (
          <TeamMemberCard
            key={member.id}
            actorId={member.id}
            name={member.name}
            actorType={member.actorType}
            presenceStatus={presenceMap.get(member.id) ?? 'OFFLINE'}
            workload={member.workload}
            maxCapacity={member.maxCapacity}
          />
        ))}
      </SimpleGrid>
    </Card>
  );
}

// ============================================================================
// GETTING STARTED CARD (DRR-073: First-run experience)
// ============================================================================

interface GettingStartedCardProps {
  viewLayer: 'admin' | 'architect';
  onSetup: () => void;
}

function GettingStartedCard({ viewLayer, onSetup }: GettingStartedCardProps) {
  const isArchitect = viewLayer === 'architect';

  return (
    <Card withBorder p="lg" style={{ borderColor: 'var(--mantine-color-blue-3)' }}>
      <Group justify="space-between" ta="left">
        <Group gap="md">
          <ThemeIcon size={48} radius="xl" color="blue" variant="light">
            <IconRocket size={24} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={3}>Getting Started</Title>
            <Text c="dimmed" size="sm">
              {isArchitect
                ? 'No workflows configured yet. Set up your tenant to get started.'
                : 'Your tenant is ready but has no workflows. Complete setup to start working.'}
            </Text>
          </Stack>
        </Group>
        <Button
          size="md"
          leftSection={<IconSettings size={18} />}
          onClick={onSetup}
        >
          Begin Setup
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }} mt="md">
        <Card withBorder p="sm">
          <Group gap="xs" mb={4}>
            <ThemeIcon size="sm" variant="light" color="blue">
              <IconUpload size={14} />
            </ThemeIcon>
            <Text size="sm" fw={600}>1. Select Product</Text>
          </Group>
          <Text size="xs" c="dimmed">
            Choose your business type and configuration
          </Text>
        </Card>
        <Card withBorder p="sm">
          <Group gap="xs" mb={4}>
            <ThemeIcon size="sm" variant="light" color="blue">
              <IconListCheck size={14} />
            </ThemeIcon>
            <Text size="sm" fw={600}>2. Configure Settings</Text>
          </Group>
          <Text size="xs" c="dimmed">
            Set up teams, roles, and compliance
          </Text>
        </Card>
        <Card withBorder p="sm">
          <Group gap="xs" mb={4}>
            <ThemeIcon size="sm" variant="light" color="green">
              <IconChecklist size={14} />
            </ThemeIcon>
            <Text size="sm" fw={600}>3. Activate Workflows</Text>
          </Group>
          <Text size="xs" c="dimmed">
            Generated automatically from your selections
          </Text>
        </Card>
      </SimpleGrid>
    </Card>
  );
}

// ============================================================================
// TENANT BEING CONFIGURED CARD (DRR-073: Admin read-only status)
// ============================================================================

/** Read-only status card for admins — white glove tenants are configured by FERP Operations. */
function TenantBeingConfiguredCard() {
  return (
    <Alert
      icon={<IconLoader size={18} />}
      title="Your tenant is being configured"
      color="blue"
      variant="light"
    >
      FERP Operations is setting up your workflows, compliance policies, and team structure.
      You will be notified when your tenant is ready.
    </Alert>
  );
}
